import type { CompanyContext } from './context';
import { moduleEnabledFor } from './context';

/**
 * 🔴 THE SIDEBAR — what the back-office offers, in the order the work happens.
 *
 * ERP Phase 1a increment 4 moved this out of `routes.tsx` UNCHANGED, so that the order and the
 * reasons recorded against it could be tested rather than only read.
 *
 * ## ⚠️ THE ORDER IS AN ARGUMENT, NOT AN ARRANGEMENT
 *
 * The comments below are the ones this list has always carried, and they are the reason it was not
 * regrouped by module when the gating landed: *"BEFORE Bills, because that is the order the purchase
 * happens in: you order it, it arrives, and only then does the supplier bill you."*
 *
 * 🔬 A sidebar grouped by module would read tidily and throw all of that away — and §1a asks for a
 * nav that is module-GATED, which is a question about what is SHOWN, not about what sits next to
 * what. The plan's assumption A1 said group by module; reading this list is what changed it.
 */
export interface NavItem {
  to: string;
  label: string;
  /**
   * 🔴 THE PERMISSION THE SCREEN'S OWN LIST ASKS FOR — design-system DS-11a4 (plan §1.6; F516).
   *
   * Any ONE of them, like `@RequirePermissions` (the guard's `required.some`, `permissions.guard.ts`). A bare resource
   * is its `:read` (`'payroll'` is `payroll:read`), which keeps 48 keys off every first load; a key with a colon is
   * itself (`'bank:cheques'`, `'payslips:self'`). ⚠️ None on Today, Reports and Settings: each is a hub whose tiles,
   * reports and cards carry their own, and none has a list of its own to be refused.
   *
   * 🔬 Checked against the server by `src/common/authz/nav-permissions.spec.ts`: the keys here must be EXACTLY the
   * ones the GET route(s) the screen lists from accept (`NAV_API` there; a handler's keys replacing its class's, as
   * the guard reads them), so a renamed or widened permission fails CI instead of hiding a section from people who
   * can read it or showing it to people who get a 403.
   */
  perm?: string | readonly string[];
}

/** The permission keys a nav item's `perm` stands for: a bare resource is its `:read`. */
export const permKeys = (perm: NavItem['perm']): string[] =>
  perm ? [perm].flat().map((p) => (p.includes(':') ? p : `${p}:read`)) : [];

export const NAV: readonly NavItem[] = [
  { to: '/', label: 'Today' },
  { to: '/invoices', label: 'Invoices', perm: 'invoices' },
  // BEFORE Bills, because that is the order the purchase happens in: you order it, it arrives,
  // and only then does the supplier bill you. ⚠️ It is also the only entry here for a document
  // that posts NOTHING, which is what its own screens say at the top.
  // BEFORE the purchase orders, because the agreement comes first and the orders draw on it.
  // The store-keeper's own documents, above the buyer's: moving stock between your own shelves
  // is a warehouse job rather than a purchasing one.
  // ERP Phase 15 increment 1: the catalogue itself, above everything that moves it.
  { to: '/items', label: 'Items', perm: 'items' },
  { to: '/stock-transfers', label: 'Stock transfers', perm: 'stock' },
  // The count before the adjustment, because that is the order the work happens in: walk the
  // aisle, then decide what the books should say about it.
  { to: '/stock-counts', label: 'Stock counts', perm: 'stock' },
  { to: '/stock-adjustments', label: 'Stock adjustments', perm: 'stock' },
  { to: '/pharma', label: 'Pharma', perm: 'pharma' },
  { to: '/boms', label: 'Bills of material', perm: 'manufacturing' },
  { to: '/subscriptions', label: 'Contracts', perm: 'subscriptions' },
  { to: '/marketplace', label: 'Daraz settlements', perm: 'marketplace' },
  // The owner's couriers (2026-09-26).
  { to: '/couriers', label: 'Courier parcels', perm: ['sales', 'payments'] },
  // ERP Phase 18: what online-store orders were asked to pay.
  { to: '/store-payments', label: 'Store payments', perm: 'payments' },
  { to: '/blanket-orders', label: 'Blanket orders', perm: 'purchasing' },
  { to: '/purchase-orders', label: 'Purchase orders', perm: 'purchasing' },
  // Between the order and the bill, which is where it sits in the work: the goods arrive, and
  // the supplier invoices afterwards.
  { to: '/goods-receipts', label: 'Goods receipts', perm: 'purchasing' },
  // With the purchasing documents rather than with banking: an LC is opened to pay a SUPPLIER,
  // and the person who opens it is the one placing the import order.
  { to: '/letters-of-credit', label: 'Letters of credit', perm: 'purchasing' },
  { to: '/export-lcs', label: 'Export LCs', perm: 'rmg' },
  // Last of the purchasing documents, because it is the one raised AFTER the goods have
  // arrived — a freight invoice that catches up with a delivery already on the shelf.
  { to: '/landed-costs', label: 'Landed costs', perm: 'purchasing' },
  { to: '/bills', label: 'Bills', perm: 'bills' },
  // Between the documents that create a balance and the ones that correct it, because that is the
  // order the work happens in: raise it, get paid for it, correct it if it was wrong.
  { to: '/payments', label: 'Receipts & payments', perm: 'payments' },
  { to: '/credit-notes', label: 'Credit notes', perm: 'credit_notes' },
  // After the money that HAS moved, because a post-dated cheque is money that has not: it sits
  // beside receipts in a reader's head and must not sit beside them in the ledger.
  { to: '/post-dated-cheques', label: 'Post-dated cheques', perm: 'bank:cheques' },
  { to: '/expenses', label: 'Expenses', perm: 'expenses' },
  { to: '/journal-drafts', label: 'Manual journals', perm: 'ledger' },
  { to: '/vat-adjustments', label: 'VAT adjustments', perm: 'vat_adjustments' },
  // The owner's decision of 2026-10-07: the enlisted company's corrections to its Mushak-9.2, beside the §45 register.
  { to: '/turnover-tax-adjustments', label: 'Turnover-tax adjustments', perm: 'turnover_tax_adjustments' },
  { to: '/partners', label: 'Customers & suppliers', perm: 'partners' },
  // ⚠️ DIRECTLY AFTER the customer list, because that is the order the relationship happens in: a
  // lead is somebody who is not a customer YET, and converting one is how the row above gets made.
  { to: '/leads', label: 'Leads', perm: 'crm' },
  // ⚠️ THE LOG, NOT THE WORKLIST. What is still DUE is `/reports/follow-ups`, because a worklist
  // read one page at a time is one somebody stops at the bottom of — the two are different screens
  // on purpose.
  { to: '/activities', label: 'Contact log', perm: 'crm' },
  // ⚠️ AFTER the contact log, because that is the order the work happens in: a deal is what a
  // logged call is ABOUT, and the board is where somebody decides which call to make next.
  { to: '/pipeline', label: 'Pipeline', perm: 'crm' },
  { to: '/projects', label: 'Projects', perm: 'projects' },
  { to: '/construction', label: 'BOQ & running bills', perm: 'construction' },
  { to: '/payroll', label: 'Payroll', perm: 'payroll' },
  // ERP Phase 12 increment 1b. Beside payroll, because the same people keep both — until 12.7 gives HR
  // its own role pack, the org spine is `payroll:*`'s.
  { to: '/organisation', label: 'Organisation', perm: ['payroll', 'hr'] },
  // ERP Phase 12 increment 2b.
  { to: '/shifts', label: 'Shifts & roster', perm: ['payroll', 'hr'] },
  // ERP Phase 12 increment 3c.
  { to: '/attendance', label: 'Attendance', perm: ['payroll', 'hr'] },
  // ERP Phase 12 increment 4.
  { to: '/holidays', label: 'Holidays', perm: ['payroll', 'hr'] },
  // The owner's Q4 (2026-09-26).
  { to: '/wage-boards', label: 'Wage boards', perm: ['payroll', 'hr'] },
  // ERP Phase 12 increment 9c.
  { to: '/loans-fines', label: 'Loans & fines', perm: 'payroll' },
  // ERP Phase 12 increment 14 — self-service.
  { to: '/my-payslips', label: 'My payslips', perm: 'payslips:self' },
  { to: '/recurring-costs', label: 'Recurring costs', perm: 'recurring' },
  // Beside the recurring costs, because both are expenses nobody raises a document for — the
  // difference is that one of them is decided by a schedule rather than by a supplier.
  { to: '/fixed-assets', label: 'Fixed assets', perm: 'fixed_assets' },
  { to: '/depreciation', label: 'Depreciation', perm: 'fixed_assets' },
  // After the documents that create them and before the reports that read them, because that is
  // what a journal is: the thing in between.
  { to: '/approvals', label: 'Waiting for you', perm: 'approval_rules' },
  { to: '/journals', label: 'Journals', perm: 'ledger' },
  { to: '/funds', label: 'Funds', perm: 'ngo' },
  { to: '/periods', label: 'Periods', perm: 'periods' },
  { to: '/bank', label: 'Bank', perm: 'bank' },
  { to: '/audit-log', label: 'Audit log', perm: 'audit' },
  // ERP Phase 17 increment 7: what the installation's operator did — beside the trail it is part of.
  { to: '/operator-activity', label: 'Operator activity', perm: 'audit' },
  // One entry, not eight. The rail lists what a person DOES; reports are read, and there are more
  // of them than of anything else — see the hub's docblock.
  { to: '/reports', label: 'Reports' },
  // Last in the rail, because it is where you go once rather than where you work. Its two most
  // important sections exist because posting routes refuse without them.
  { to: '/settings', label: 'Settings' },
] as const;

/**
 * 🔴 THE ITEMS THIS COMPANY MAY ACTUALLY SEE — §1a's second exit criterion, first half.
 *
 * ⚠️ ABSENT, NOT GREYED. A greyed row advertises what the client has not bought, every time they
 * look at it, and invites a conversation the person reading the nav cannot have.
 *
 * 🔬 A context still loading, or one that could not be read, shows EVERYTHING. An empty nav for a
 * moment on every page load — or whenever the network hiccups — looks exactly like the product
 * having lost the customer's data, and the API refuses anything they have not bought regardless.
 * This decides what is SHOWN; `ModuleGuard` on the server is the gate.
 */
export function visibleNav(context: CompanyContext | null | undefined): readonly NavItem[] {
  /**
   * 🔴 AND WHAT THIS PERSON MAY READ — DS-11a4 (plan §1.6; owner, 2026-10-07: no "Show all" for administrators). A
   * section whose list the API would refuse them is absent too, from the sidebar and the palette, which share these
   * groups. The same two unknowns answer yes: no context, or an item with no `perm`. A typed URL still opens the
   * screen, and its refusal now says who can grant it (`ErrorState`).
   */
  return NAV.filter((item) => {
    const keys = permKeys(item.perm);
    return moduleEnabledFor(item.to, context) && (!keys.length || !context || keys.some((k) => context.permissions.includes(k)));
  });
}
